import { useEffect, useRef, useState } from "react";
import { FiAlertCircle, FiFilter } from "react-icons/fi";
import { GiCow } from "react-icons/gi";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioControleEstoqueAnimais = () => {
  const [dados, setDados] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const [lotes, setLotes] = useState([]);
  const [fazendas, setFazendas] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    lotes: useRef(null),
    fazendas: useRef(null)
  };

  const [filtros, setFiltros] = useState({
    lotes: [],
    fazendas: [],
    data_inicio: "",
    data_fim: "",
    status: "" // "ativo", "saida", "todos"
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs[openDropdown]?.current && !dropdownRefs[openDropdown].current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
        const [lotesRes, fazendasRes] = await Promise.all([
        api.get("/pecuaria/lotes/"),
        api.get("/fazendas/fazendas/")
        ]);

        setLotes(lotesRes.data.results || []);
        setFazendas(fazendasRes.data.results || []);
    } catch (error) {
        console.error("Erro ao carregar opções:", error);
        setLotes([]);
        setFazendas([]);
    }
    };

  const handleMultiSelect = (campo, id) => {
    setFiltros(prev => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options)) {
      const allIds = options.map(opt => opt.id);
      setFiltros(prev => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros(prev => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value
    });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");

    try {
      const params = {};

      if (filtros.lotes && filtros.lotes.length > 0) {
        params.lotes = filtros.lotes.join(",");
      }
      if (filtros.fazendas && filtros.fazendas.length > 0) {
        params.fazendas = filtros.fazendas.join(",");
      }
      if (filtros.data_inicio) {
        params.data_inicio = filtros.data_inicio;
      }
      if (filtros.data_fim) {
        params.data_fim = filtros.data_fim;
      }
      if (filtros.status) {
        params.status = filtros.status;
      }

      console.log("🌐 URL:", `/relatorios/pecuarios/estoque-animais/?${new URLSearchParams(params)}`);

      const response = await api.get("/relatorios/pecuarios/estoque-animais/", {
        params: params
      });

      console.log("🔍 Dados recebidos:", response.data);
      setDados(response.data);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      lotes: [],
      fazendas: [],
      data_inicio: "",
      data_fim: "",
      status: ""
    });
  };

  const exportarExcel = () => {
    if (!dados || dados.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    // "historico" é uma lista de objetos ({data, evento, descricao, documento})
    // — json_to_sheet não sabe serializar isso, viraria "[object Object]" na
    // planilha. Achata para texto legível antes de exportar.
    const dadosParaExportar = dados.map(({ historico, ...resto }) => ({
      ...resto,
      historico: (historico || [])
        .map((h) => `${h.data} - ${h.evento}: ${h.descricao}`)
        .join(" | "),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Controle Estoque Animais"
    );

    XLSX.writeFile(workbook, "relatorio_estoque_animais.xlsx");
  };

  const exportarPDF = async () => {
    if (!dados || dados.length === 0) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const colunas = [
      { key: "identificacao", label: "Identificação" },
      { key: "lote", label: "Lote" },
      { key: "categoria", label: "Categoria" },
      { key: "data_entrada", label: "Data Entrada" },
      { key: "data_saida", label: "Data Saída" },
      { key: "origem", label: "Origem" },
      { key: "destino", label: "Destino" },
      { key: "peso_atual", label: "Peso Atual (kg)" },
      { key: "status", label: "Status" },
      { key: "codigo_rastreabilidade", label: "Rastreabilidade" },
    ];

    await exportarRelatorioPDF({
      titulo: "Relatório de Controle de Estoque de Animais",
      colunas,
      dados,
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
      formatarValor: (valor) => (valor === null || valor === undefined || valor === "" ? "-" : String(valor)),
    });
  };

  const imprimir = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Controle de Estoque de Animais</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 18px; }
            .header-info { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
            .historico { margin-top: 30px; }
            .historico h3 { font-size: 14px; margin-bottom: 10px; }
            .historico-item { margin-bottom: 15px; padding: 10px; background: #f9f9f9; border-left: 3px solid #3498db; }
            .historico-item p { margin: 5px 0; font-size: 10px; }
            @media print {
              body { margin: 0; padding: 20px; }
              .historico-item { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Relatorio de Controle de Estoque de Animais</h1>
          <div class="header-info">
            <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Total de registros:</strong> ${dados.length}</p>
          </div>

          <table>
            <thead>
              <tr>
                <th>IDENTIFICACAO</th>
                <th>LOTE</th>
                <th>CATEGORIA</th>
                <th>DATA ENTRADA</th>
                <th>DATA SAIDA</th>
                <th>ORIGEM</th>
                <th>DESTINO</th>
                <th>PESO ATUAL</th>
                <th>STATUS</th>
                <th>COD. RASTREABILIDADE</th>
              </tr>
            </thead>
            <tbody>
              ${dados.map(item => `
                <tr>
                  <td>${item.identificacao || '-'}</td>
                  <td>${item.lote || '-'}</td>
                  <td>${item.categoria || '-'}</td>
                  <td>${item.data_entrada || '-'}</td>
                  <td>${item.data_saida || '-'}</td>
                  <td>${item.origem || '-'}</td>
                  <td>${item.destino || '-'}</td>
                  <td>${item.peso_atual || '-'}</td>
                  <td>${item.status || '-'}</td>
                  <td>${item.codigo_rastreabilidade || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <!-- Histórico completo dos animais -->
          <div class="historico">
            <h3>📋 Histórico Completo dos Animais</h3>
            ${dados.map(animal => `
              <div class="historico-item">
                <p><strong>🐄 Animal:</strong> ${animal.identificacao} (${animal.lote})</p>
                <p><strong>📅 Histórico:</strong></p>
                <ul>
                  ${animal.historico && animal.historico.length > 0
                    ? animal.historico.map(ev => `<li>${ev.data} - ${ev.evento}: ${ev.descricao}</li>`).join('')
                    : '<li>Nenhum evento registrado</li>'
                  }
                </ul>
              </div>
            `).join('')}
          </div>

          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Componente de Select Multiplo
  const MultiSelect = ({ label, options, selectedValues, fieldName, refProp }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter(opt => selectedArray.includes(opt.id));
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2) return selected.map(s => s.nome).join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() => setOpenDropdown(openDropdown === fieldName ? null : fieldName)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-brand-green focus:border-transparent"
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>

        {openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll(fieldName);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll(fieldName, optionsArray);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                Nenhuma opção disponível
              </div>
            ) : (
              optionsArray.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedArray.includes(option.id)}
                    onChange={() => handleMultiSelect(fieldName, option.id)}
                    className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className="text-gray-700">{option.nome}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={GiCow}
            titulo="Relatório de Controle de Estoque de Animais"
            descricao="Consulte o estoque de animais, rastreabilidade e histórico completo"
          />

          {/* Filtros */}
          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Lotes - Multi Select */}
              <MultiSelect
                label="Lotes"
                options={lotes}
                selectedValues={filtros.lotes}
                fieldName="lotes"
                refProp={dropdownRefs.lotes}
              />

              {/* Fazendas - Multi Select */}
              <MultiSelect
                label="Fazendas"
                options={fazendas}
                selectedValues={filtros.fazendas}
                fieldName="fazendas"
                refProp={dropdownRefs.fazendas}
              />

              {/* Status - Select */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={filtros.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition bg-white"
                >
                  <option value="">Todos</option>
                  <option value="ativo">Ativo (na fazenda)</option>
                  <option value="saida">Vendido/Saída</option>
                  <option value="morte">Morte</option>
                </select>
              </div>

              {/* Data Inicio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Inicio (Entrada)
                </label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>

              {/* Data Fim */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Fim (Entrada)
                </label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {/* Filtros Ativos - Tags */}
            {((filtros.lotes && filtros.lotes.length > 0) ||
              (filtros.fazendas && filtros.fazendas.length > 0)) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filtros Selecionados
                </label>
                <div className="flex flex-wrap gap-2">
                  {filtros.lotes && filtros.lotes.map((id) => {
                    const lote = lotes.find(l => l.id === id);
                    return lote ? (
                      <span key={`lote-${id}`} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Lote: {lote.nome}
                        <button
                          onClick={() => handleMultiSelect("lotes", id)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  {filtros.fazendas && filtros.fazendas.map((id) => {
                    const fazenda = fazendas.find(f => f.id === id);
                    return fazenda ? (
                      <span key={`fazenda-${id}`} className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Fazenda: {fazenda.nome}
                        <button
                          onClick={() => handleMultiSelect("fazendas", id)}
                          className="ml-1 text-purple-600 hover:text-purple-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {erro && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            {/* Botoes */}
            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            overflow: 'auto'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{
              zIndex: 10000,
              maxHeight: 'calc(100vh - 4rem)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <GiCow size={22} className="text-brand-green" />
                  Relatorio de Controle de Estoque de Animais
                </h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition flex-shrink-0"
              >
                <span className="text-xl">✕</span>
              </button>
            </div>

            {/* Acoes */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3 flex-shrink-0">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar para Excel
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar para PDF
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            {/* Tabela */}
            <div className="flex-1 overflow-auto p-6" style={{ minHeight: 0 }}>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex flex-wrap justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-800 font-medium">Total de registros:</span>
                  <span className="text-emerald-900 font-bold text-lg">{dados.length}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-emerald-800">Gerado em:</span>
                  <span className="text-emerald-900">{new Date().toLocaleString()}</span>
                </div>
              </div>

              {dados.length > 0 ? (
                <div className="space-y-6">
                  {/* Tabela resumo */}
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <div style={{ minWidth: 'max-content' }}>
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Identificação</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Lote</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Categoria</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Entrada</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Saída</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Origem</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Destino</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Peso (kg)</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Rastreabilidade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.map((item, index) => (
                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.identificacao || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.lote || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.categoria || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data_entrada || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.data_saida || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.origem || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.destino || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{item.peso_atual || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  item.status === 'ativo' ? 'bg-green-100 text-green-800' :
                                  item.status === 'saida' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {item.status || "-"}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                <span className="font-mono text-xs">{item.codigo_rastreabilidade || "-"}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Histórico Completo */}
                  <div className="mt-8">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      📋 Histórico Completo dos Animais
                    </h3>
                    <div className="space-y-4">
                      {dados.map((animal, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-gray-800">
                              🐄 {animal.identificacao} - {animal.lote}
                            </h4>
                            <span className="text-xs text-gray-500">Código: {animal.codigo_rastreabilidade}</span>
                          </div>
                          {animal.historico && animal.historico.length > 0 ? (
                            <div className="space-y-2">
                              {animal.historico.map((evento, eidx) => (
                                <div key={eidx} className="flex items-start gap-3 text-sm">
                                  <span className="text-blue-600 font-mono whitespace-nowrap">{evento.data}</span>
                                  <span className="text-gray-800 font-medium min-w-[100px]">{evento.evento}:</span>
                                  <span className="text-gray-600">{evento.descricao}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm italic">Nenhum evento registrado no histórico</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <EstadoVazio
                  titulo="Nenhum animal encontrado"
                  descricao="Ajuste os filtros selecionados e tente novamente."
                />
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioControleEstoqueAnimais;
